import { useEffect, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Users, Info, ClipboardCheck, Archive, Radio } from "lucide-react";
import { getCandidatePool, getInterviewSessions, inviteToSession } from "@/lib/api";
import { Rid, StatusPill, DaysLeft, Banner, CompletedState } from "@/components/ui-bits";
import { OperationalStatusRail } from "@/components/OperationalStatusRail";
import { toast } from "sonner";

function useQuerySegment() {
  const { search } = useLocation();
  return new URLSearchParams(search).get("segment");
}

function Crumb() {
  return (
    <div className="crumb">
      <Link to="/dashboard">Staffing Home</Link> › <strong>Candidate Pool</strong>
    </div>
  );
}

function PoolRow({ c, showHold, selectable, selected, onToggle }) {
  const navigate = useNavigate();
  const open = () => navigate(`/candidates/${c.registrationId}`);
  const rowClick = (e) => {
    if (e.target.closest("a, button, input, label")) return;
    open();
  };
  const shortId = c.registrationId.slice(-3);
  return (
    <tr className="row-click" data-testid={`pool-row-${c.registrationId}`} onClick={rowClick}>
      <td className="cb">
        {selectable ? (
          <input type="checkbox" aria-label="Select candidate" checked={!!selected}
            onChange={() => onToggle(c.registrationId)} onClick={(e) => e.stopPropagation()}
            data-testid={`pool-select-${c.registrationId}`} />
        ) : (
          <input type="checkbox" aria-label="Select" onClick={(e) => e.stopPropagation()} />
        )}
      </td>
      <td className="cand">
        <span className="cand-line">
          <span className="quick-id" title={c.registrationId}>{shortId}</span>
          <Link className="cand-name" to={`/candidates/${c.registrationId}`} data-testid={`candidate-link-${c.registrationId}`}>{c.name}</Link>
          <span className="cand-fullid">{c.registrationId}</span>
        </span>
      </td>
      <td data-label="Days left"><DaysLeft days={c.daysLeftInPool} total={c.poolDays} /></td>
      <td data-label="Preferred shift">{c.preferredShift}</td>
      <td data-label="Résumé">{c.hasResume ? <Link className="tbl-link" to={`/candidates/${c.registrationId}`}>View</Link> : <span className="mut">None</span>}</td>
      <td data-label="Preview">{c.previewCompleted ? <CompletedState label="Completed" testid={`preview-${c.registrationId}`} /> : <span className="mut">—</span>}</td>
      <td data-label="Interest">{c.stillInterested ? c.stillInterested : <span className="mut">—</span>}</td>
      <td className="status" data-label="Status">
        <StatusPill status={c.status} label={c.statusLabel} />
        {c.onboarding?.started && (
          <span className="mut" data-testid={`onb-progress-${c.registrationId}`} style={{ display: "block", fontSize: ".72rem", marginTop: 4 }}>
            {c.onboarding.mihpComplete ? "MIHP Requirements Complete ✓ · ADP Setup Pending" : `Onboarding — ${c.onboarding.items.filter((i) => i.status === "complete").length}/${c.onboarding.items.length}`}
          </span>
        )}
      </td>
    </tr>
  );
}

const TABLE_HEAD = (
  <thead>
    <tr>
      <th style={{ width: 34 }}><input type="checkbox" aria-label="Select all" /></th>
      <th>Candidate</th>
      <th>Days left in pool</th>
      <th>Preferred shift</th>
      <th>Résumé</th>
      <th>Preview</th>
      <th>Interest</th>
      <th>Status</th>
    </tr>
  </thead>
);

function onboardState(c) {
  const onb = c.onboarding;
  if (!onb || !onb.started) return { label: "Begin onboarding", cls: "start", glyph: "→" };
  if (!onb.mihpComplete) {
    const done = (onb.items || []).filter((i) => i.status === "complete").length;
    return { label: `Onboarding — ${done}/${(onb.items || []).length}`, cls: "prog", glyph: "→" };
  }
  if (c.convertedToEmployee) return { label: "Employee ✓", cls: "done", glyph: "" };
  const adp = c.adp;
  if (!adp || adp.status === "not_started") return { label: "MIHP Complete ✓ · ADP Not Started", cls: "done", glyph: "" };
  if (adp.status === "complete") return { label: "ADP Setup Complete ✓", cls: "done", glyph: "" };
  return { label: "ADP Setup In Progress", cls: "prog", glyph: "→" };
}

function HireApprovedRoster({ data, active }) {
  const navigate = useNavigate();
  const rows = data.groups.hire_approved || [];
  return (
    <div data-testid="candidate-pool">
      <Crumb />
      <div className="segs-head">Filter by segment</div>
      <OperationalStatusRail
        items={data.segments.map((s) => ({ ...s }))}
        activeKey={active}
        getHref={(k) => `/candidate-pool?segment=${k}`}
        label={null}
      />
      <div className="panel list-panel" data-testid="section-hire-approved">
        <div className="ph"><h2>Hire Approved <span className="mut">· {rows.length} candidates</span></h2></div>
        <div className="pb">
          <p className="mut" style={{ margin: "0 0 8px" }}>Employers approved these hires. Open a candidate to begin Digital Onboarding.</p>
          <div className="mh-list hire-list" data-testid="hire-roster">
            {rows.map((c, i) => {
              const st = onboardState(c);
              const meta = [c.position, c.hireShift, c.startDateLabel].filter(Boolean).join(" · ");
              return (
                <button key={c.id} className="mh-row" data-testid={`hire-row-${c.registrationId}`} onClick={() => navigate(`/candidates/${c.registrationId}`)}>
                  <span className="mh-ord">{i + 1}</span>
                  <span className="mh-rowmain">
                    <span className="mh-name">{c.name}</span>
                    {meta && <span className="mh-rec" data-testid={`hire-meta-${c.registrationId}`}>{meta}</span>}
                  </span>
                  <span className={`mh-hire-state ${st.cls}`} data-testid={`hire-state-${c.registrationId}`}>{st.label} {st.glyph}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CandidatePool() {
  const [data, setData] = useState(null);
  const raw = useQuerySegment() || "waiting";
  const valid = ["waiting", "future_consideration", "hire_approved", "archived"];
  const active = valid.includes(raw) ? raw : "waiting";

  const [sel, setSel] = useState(() => new Set());
  const [showInvite, setShowInvite] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [target, setTarget] = useState("");
  const [newDate, setNewDate] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = () => getCandidatePool().then(setData).catch(() => {});

  useEffect(() => {
    reload();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [active]);

  const toggle = (rid) => setSel((s) => {
    const n = new Set(s);
    n.has(rid) ? n.delete(rid) : n.add(rid);
    return n;
  });

  const openInvite = async () => {
    if (sel.size === 0) return;
    try {
      const all = await getInterviewSessions();
      const activeSessions = (all || []).filter((s) => s.status === "live" || s.status === "upcoming");
      setSessions(activeSessions);
      setTarget(activeSessions[0]?.id || "new");
      setShowInvite(true);
    } catch {
      toast.error("Could not load interview sessions.");
    }
  };

  const confirmInvite = async () => {
    if (busy) return;
    if (target === "new" && !newDate) { toast.error("Choose a date for the new session."); return; }
    const payload = target === "new" ? { sessionDate: newDate } : { sessionId: target };
    setBusy(true);
    try {
      const rids = Array.from(sel);
      for (const rid of rids) { await inviteToSession(rid, payload); }
      toast.success(`Invited ${rids.length} candidate${rids.length > 1 ? "s" : ""} to the interview session.`);
      setSel(new Set());
      setShowInvite(false);
      setNewDate("");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not invite. Please try again.");
    }
    setBusy(false);
  };

  if (!data) return <div className="empty" data-testid="pool-loading">Loading candidate pool…</div>;

  const g = data.groups;
  const allWaitingSelected = g.waiting.length > 0 && g.waiting.every((c) => sel.has(c.registrationId));
  const toggleAllWaiting = () => setSel((s) => {
    if (g.waiting.every((c) => s.has(c.registrationId))) return new Set();
    return new Set(g.waiting.map((c) => c.registrationId));
  });

  if (active === "hire_approved") return <HireApprovedRoster data={data} active={active} />;

  return (
    <div data-testid="candidate-pool">
      <Crumb />

      <div className="segs-head">Filter by segment</div>
      <OperationalStatusRail
        items={data.segments.map((s) => ({
          ...s,
          live: s.count > 0 && s.key === "waiting",
          liveKind: "breathing",
        }))}
        activeKey={active}
        getHref={(k) => `/candidate-pool?segment=${k}`}
        label={null}
      />

      <Banner variant="neutral" icon={<Users size={18} />}>
        Active candidate pool is currently <strong>{data.poolDays} days</strong>, set in Staffing
        Settings. Candidates who reach the end of the pool move to <strong>Archived — Inactive</strong>.
        They are never deleted.
      </Banner>

      {active === "waiting" && (
        <>
          {/* Interview Pending */}
          <div className="panel list-panel" data-testid="section-waiting">
            <div className="ph">
              <h2>Interview Pending <span className="mut">· {g.waiting.length} candidates</span></h2>
              <span className="sp" />
              <button className="ph-link" data-testid="invite-selected-btn" disabled={sel.size === 0} onClick={openInvite}>
                Invite selected to a session{sel.size ? ` (${sel.size})` : ""}
              </button>
            </div>
            <div className="pb">
              <p className="mut" style={{ margin: "0 0 6px" }} data-testid="waiting-help">
                Candidates here are <strong>Interview Pending</strong> and not yet assigned to an interview session.
                Review or contact them, then select and invite them to a session — that is when they appear on the
                supervisor interview roster. Current Status stays Interview Pending until an interview decision is recorded.
              </p>
            </div>
            <div className="pb flush">
              <table className="grid-tbl">
                <thead>
                  <tr>
                    <th style={{ width: 34 }}>
                      <input type="checkbox" aria-label="Select all pending" checked={allWaitingSelected}
                        onChange={toggleAllWaiting} data-testid="pool-select-all" />
                    </th>
                    <th>Candidate</th>
                    <th>Days left in pool</th>
                    <th>Preferred shift</th>
                    <th>Résumé</th>
                    <th>Preview</th>
                    <th>Interest</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>{g.waiting.map((c) => (
                  <PoolRow key={c.id} c={c} selectable selected={sel.has(c.registrationId)} onToggle={toggle} />
                ))}</tbody>
              </table>
            </div>
          </div>

          {/* Live Interview Intake */}
          <div className="panel list-panel" data-testid="section-live-intake">
            <div className="ph">
              <h2>LIVE INTERVIEW INTAKE <span className="mut">— {(g.live_intake || []).length}</span></h2>
              <span className="sp" />
              {(g.live_intake || []).length > 0 && <span className="pill waiting pill-active"><Radio size={12} /> Live</span>}
            </div>
            <div className="pb">
              <Banner variant="neutral" icon={<Radio size={18} />} style={{ marginBottom: 14 }}>
                A live, filtered view of candidates in the currently <strong>open same-day interview intake</strong> — the same canonical Candidate Records, never duplicates. Candidates leave this view once their interview decision is recorded, while remaining in the normal Candidate Pool lifecycle.
              </Banner>
              {(g.live_intake || []).length === 0 ? (
                <div className="mut" data-testid="live-intake-empty">No same-day interview intake is open right now.</div>
              ) : (
                <table className="grid-tbl">
                  {TABLE_HEAD}
                  <tbody>{g.live_intake.map((c) => <PoolRow key={c.id} c={c} />)}</tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {active === "future_consideration" && (
        <div className="panel list-panel" data-testid="section-future-consideration">
          <div className="ph">
            <h2>Future Consideration <span className="mut">· {g.future_consideration.length} candidates</span></h2>
            <span className="sp" />
            <span className="ph-link">About this pool</span>
          </div>
          <div className="pb">
            <Banner variant="neutral" icon={<ClipboardCheck size={18} />} style={{ margin: 0 }}>
              These candidates <strong>already completed their Interview + Factory Preview</strong> and were
              <strong> not rejected</strong>. There was no current vacancy at decision time, so they are held
              as ready, previously-interviewed people to reconsider when a suitable vacancy or shift opens.
              They deliberately do <strong>not</strong> return to Interview Pending — they have already interviewed.
            </Banner>
          </div>
          <div className="pb flush">
            <table className="grid-tbl">
              {TABLE_HEAD}
              <tbody>{g.future_consideration.map((c) => <PoolRow key={c.id} c={c} />)}</tbody>
            </table>
          </div>
        </div>
      )}

      {active === "archived" && (
        <div className="panel list-panel" data-testid="section-archived">
          <div className="ph">
            <h2>Archived — Inactive Pool <span className="mut">· {g.archived.length} candidates</span></h2>
            <span className="sp" />
          </div>
          <div className="pb">
            <Banner variant="neutral" icon={<Archive size={18} />} style={{ marginBottom: 14 }}>
              Candidates who reached the end of the active pool. Their registration, interview history and
              audit trail are <strong>fully preserved</strong> — nothing is deleted. Résumé files follow the
              approved archival handling and are no longer downloadable, while the record that a résumé
              existed is retained. MIHP can deliberately reactivate anyone here.
            </Banner>
            <table>
              <thead>
                <tr>
                  <th>Registration</th><th>Candidate</th><th>Archived</th>
                  <th>Reason</th><th>History</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {g.archived.map((c) => (
                  <tr key={c.id} data-testid={`archived-row-${c.registrationId}`}>
                    <td data-label="Registration"><Rid registrationId={c.registrationId} /></td>
                    <td data-label="Candidate"><span className="nm">{c.name}</span></td>
                    <td data-label={c.archivedDateLabel ? "Archived" : ""}>{c.archivedDateLabel}</td>
                    <td className="mut" data-label="Reason">{c.archivedReason}</td>
                    <td data-label="History"><CompletedState label="Preserved" /></td>
                    <td data-label=""><button className="btn sm">Reactivate</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="note-compact" data-testid="pool-scope-guard">
        <Info size={15} />
        <span>
          <strong>Scope guard.</strong> Availability context only — days remaining, preferred shift,
          résumé presence, preview completion, hold status and stated interest. There is deliberately no
          scoring, ranking, rating or automated matching.
        </span>
      </div>

      {showInvite && (
        <div className="modal-scrim" data-testid="invite-modal" onClick={() => !busy && setShowInvite(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-head"><h2>Invite {sel.size} candidate{sel.size > 1 ? "s" : ""} to a session</h2></div>
            <div className="modal-body">
              <p className="modal-intro">
                Assigning to a session makes the selected candidate{sel.size > 1 ? "s" : ""} visible on that
                session's supervisor interview roster. Current Status stays <strong>Interview Pending</strong>.
                The same Candidate ID is preserved — no duplicate record is created.
              </p>
              <div className="invite-opts" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {sessions.map((s) => (
                  <label key={s.id} className="invite-opt" data-testid={`invite-session-${s.id}`}
                    style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
                    <input type="radio" name="invite-target" checked={target === s.id} onChange={() => setTarget(s.id)} />
                    <span>{s.employerName} — {s.interviewDateLabel}
                      <span className="mut"> · {s.status === "live" ? "Live" : "Upcoming"} · {s.sessionCode}</span>
                    </span>
                  </label>
                ))}
                <label className="invite-opt" data-testid="invite-session-new"
                  style={{ display: "flex", gap: 10, alignItems: "center", cursor: "pointer" }}>
                  <input type="radio" name="invite-target" checked={target === "new"} onChange={() => setTarget("new")} />
                  <span>Create a new interview session for a date</span>
                </label>
                {target === "new" && (
                  <input type="date" className="ca-input" value={newDate} onChange={(e) => setNewDate(e.target.value)}
                    data-testid="invite-new-date" style={{ maxWidth: 220 }} />
                )}
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={() => setShowInvite(false)} disabled={busy} data-testid="invite-cancel">Cancel</button>
              <button className="btn primary" onClick={confirmInvite} disabled={busy} data-testid="invite-confirm">
                {busy ? "Inviting…" : "Invite to session"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
